/**
 * The config documents on the wire (D22, D32) — a show style's, a studio's,
 * a production's — are JSON objects the contract carries OPAQUELY: what a
 * valid one is belongs to the engine (`@acp/engine/showStyle.js`,
 * `studio.js`, `productionConfig.js`), and the handlers validate with an
 * injected validator, so the contract never drifts from the engine. The
 * contract bounds only their size, so every host refuses the same oversized
 * document.
 *
 * THE SIZE BUDGET. A production record carries the resolved config it runs
 * AND the snapshot of the show style and studio it came from (D32), and it
 * must fit both a DynamoDB item (400 KB) and the cloud's request body (256
 * KiB — a node pushes production records with `PUT /node/productions/{id}`):
 *
 *   show style   ≤  80 000 characters
 *   studio       ≤  40 000
 *   → snapshot   ≤ 120 000, and the resolved config (the two merged) about as much
 *   production config ≤ 200 000 — the pre-D32 bound, kept so a production created
 *                before the split (whose config was a whole studio config) still reads
 */
import { jsonValueSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';

export const MAX_SHOW_STYLE_CONFIG_CHARS = 80_000;
export const MAX_STUDIO_CONFIG_CHARS = 40_000;
export const MAX_PRODUCTION_CONFIG_CHARS = 200_000;

/** A JSON object of at most `maxChars` serialised characters; `what` names it in the refusal. */
export function configDocumentWireSchema(maxChars: number, what: string) {
  return z.record(z.string(), jsonValueSchema).refine((config) => JSON.stringify(config).length <= maxChars, {
    message: `the ${what} serialises to more than ${String(maxChars)} characters`,
  });
}

/** Any config document: a JSON object. */
export type ConfigDocumentWire = z.infer<ReturnType<typeof configDocumentWireSchema>>;
